import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useAnimation } from "../../hooks/useAnimate";
import Section from "../layout/Section";
import { TitleHeading } from "../typography/TitleHeading";
import BodyText from "../typography/BodyText";
import _ from "lodash";
import { Masonry } from "../Masonry";
import { Theme, useThemed } from "../../hooks/useTheme";
import { twMerge } from "tailwind-merge";
import { graphql, useStaticQuery } from "gatsby";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/solid";

export const TestimonialsSlider = (props: { theme?: Theme }) => {
  const { id, animate } = useAnimation();
  const { t, i18n } = useTranslation();
  const themed = useThemed(props.theme);
  const [current, setCurrent] = React.useState(0);

  const {
    allTestimonialsJson: { nodes: testimonials },
  } = useStaticQuery<{
    allTestimonialsJson: {
      nodes: {
        name: string;
        role: string;
        company: string;
        image: string;
        comment: {
          it: string;
          en: string;
          de: string;
        };
      }[];
    };
  }>(graphql`
    query ciccio {
      allTestimonialsJson(
        sort: { fields: order }
        filter: { published: { ne: false } }
      ) {
        nodes {
          role
          name
          image
          company
          comment {
            de
            en
            it
          }
        }
      }
    }
  `);

  const testimonial = React.useMemo(() => testimonials[current], [current]);

  return (
    <Section id={id} theme={props.theme}>
      <div id="testimonials" {...animate("fade-down")}>
        <TitleHeading
          center
          bgTag
          theme={props.theme}
          bgTagText={t("common:testimonials.bgTag")}
          tag={t("common:testimonials.tag")}
          title={t("common:testimonials.title")}
        />
        <BodyText
          className="text-center max-w-4xl m-auto"
          dark={props.theme === "dark" || props.theme === "indigo"}
        >
          {t("common:testimonials.description")}
        </BodyText>
      </div>
      <div className="mt-12" {...animate("fade-up")}>
        <div className="flex justify-between md:space-x-4">
          <button
            onClick={() =>
              setCurrent(
                (current - 1 + testimonials.length) % testimonials.length,
              )
            }
            className="hidden md:block px-4 border border-transparent hover:border-indigo-300 hover:bg-indigo-100 transition-colors rounded-2xl"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div className="flex items-center justify-center min-h-[28rem]">
            <div className="bg-gray-100 py-8 px-4 lg:px-24 h-full flex justify-center items-center">
              <div
                className={twMerge(
                  "w-full flex flex-col items-start max-w-2xl mx-auto ",
                )}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="50"
                  height="50"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.09 11.6314H3.4C3.48 6.96144 4.4 6.19144 7.27 4.49144C7.6 4.29144 7.71 3.87144 7.51 3.53144C7.32 3.20144 6.89 3.09144 6.56 3.29144C3.18 5.29144 2 6.51144 2 12.3214V17.7114C2 19.4214 3.39 20.8014 5.09 20.8014H8.09C9.85 20.8014 11.18 19.4714 11.18 17.7114V14.7114C11.18 12.9614 9.85 11.6314 8.09 11.6314Z"
                    fill="#eb611c"
                  />
                  <path
                    d="M18.9086 11.6314H14.2186C14.2986 6.96144 15.2186 6.19144 18.0886 4.49144C18.4186 4.29144 18.5286 3.87144 18.3286 3.53144C18.1286 3.20144 17.7086 3.09144 17.3686 3.29144C13.9886 5.29144 12.8086 6.51144 12.8086 12.3314V17.7214C12.8086 19.4314 14.1986 20.8114 15.8986 20.8114H18.8986C20.6586 20.8114 21.9886 19.4814 21.9886 17.7214V14.7214C21.9986 12.9614 20.6686 11.6314 18.9086 11.6314Z"
                    fill="#eb611c"
                  />
                </svg>
                <p
                  className={twMerge(
                    "text-lg mt-3 leading-tight",
                    themed({
                      white: "text-gray-800",
                      gray: "text-gray-900",
                      indigo: "text-indigo-100",
                      dark: "text-gray-100",
                    }),
                  )}
                >
                  {testimonial.comment[i18n.language]}
                </p>
                <div className="mt-4">
                  <p
                    className={twMerge(
                      "text-lg font-bold font-grotesque",
                      themed({
                        white: "text-gray-900",
                        gray: "text-gray-900",
                        indigo: "text-gray-100",
                        dark: "text-gray-100",
                      }),
                    )}
                  >
                    {testimonial.name}
                  </p>
                  <p
                    className={twMerge(
                      "text-sm text-gray-500",
                      themed({
                        white: "text-gray-900",
                        gray: "text-gray-900",
                        indigo: "text-gray-100",
                        dark: "text-gray-100",
                      }),
                    )}
                  >
                    {testimonial.role}, <i>{testimonial.company}</i>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => setCurrent((current + 1) % testimonials.length)}
            className="hidden md:block px-4 border border-transparent hover:border-indigo-300 hover:bg-indigo-100 transition-colors rounded-2xl"
          >
            <ArrowRightIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="flex justify-center flex-wrap space-x-3 mt-6">
          {testimonials.map((testimonial, i) => (
            <button
              className={twMerge(
                "p-1 border-2 border-transparent rounded-full transition-colors",
                i === current && "border-primary",
              )}
              onClick={() => setCurrent(i)}
            >
              <img
                className="h-8 md:h-12 w-8 md:w-12 rounded-full"
                src={
                  require(
                    `../../assets/images/testimonials/${testimonial.image}`,
                  ).default
                }
                alt={testimonial.name}
              />
            </button>
          ))}
        </div>
      </div>
    </Section>
  );
};
